import React, { useEffect, useState } from 'react'
import SideBar from '../components/SideBar'
import DetailsHeaderBar from '../components/DetailsHeaderBar'
import { useNavigate, useParams } from 'react-router-dom';
import Select from "react-select";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { categoryService } from '../services/categoryService';

function EditSubCategory() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [categories, setCategories] = useState([]);
    const [categoryMasterOptions, setCategoryMasterOptions] = useState([]);

    const [images, setImages] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [originalData, setOriginalData] = useState(null);

    const [subCategoryName, setSubCategoryName] = useState("");
    const [slug, setSlug] = useState("");
    const [description, setDescription] = useState("");
    const [categoryId, setCategoryId] = useState(null);
    const [categoryMasterId, setCategoryMasterId] = useState(null);
    const [keywordInput, setKeywordInput] = useState("");

    const statusOptions = [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
    ];

    const [status, setStatus] = useState(statusOptions[0]);

    useEffect(() => {
        fetchSubcategoryDetails();
        fetchCategoriesList();
    }, [id]);

    const fetchCategoriesList = async () => {
        try {
            const response = await categoryService.getAllCategories({ per_page: 100, status: 'active' });
            if (response.categories) {
                const categoryOptions = response.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }));
                setCategories(categoryOptions);
                // Use the same categories for category master for now
                setCategoryMasterOptions(categoryOptions);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchSubcategoryDetails = async () => {
        try {
            setFetching(true);
            const response = await categoryService.getSubcategory(id);
            const subcategory = response.subcategory;
            
            console.log('Fetched subcategory:', subcategory);
            
            // Store original data for comparison
            setOriginalData({
                name: subcategory.name || "",
                slug: subcategory.slug || "",
                description: subcategory.description || "",
                status: subcategory.status || "active",
                category_id: subcategory.category_id,
                category_master_id: subcategory.category_master_id,
                image: subcategory.image_url || subcategory.image,
                image_url: subcategory.image_url || subcategory.image,
            });
            
            // Set form values from API response
            setSubCategoryName(subcategory.name || "");
            setSlug(subcategory.slug || "");
            setDescription(subcategory.description || "");
            setCategoryId(subcategory.category_id);
            setCategoryMasterId(subcategory.category_master_id);

            // Set status
            if (subcategory.status) {
                const statusOption = statusOptions.find(opt => opt.value === subcategory.status);
                setStatus(statusOption || statusOptions[0]);
            }
            
            // Set image if exists
            if (subcategory.image_url || subcategory.image) {
                const imageUrl = subcategory.image_url || subcategory.image;
                setImages([{
                    preview: imageUrl,
                    file: null,
                    isExisting: true,
                    originalUrl: imageUrl,
                    fileName: imageUrl.split('/').pop() || 'existing-image.jpg'
                }]);
            }
        } catch (error) {
            console.error('Failed to fetch subcategory details:', error);
            alert(error.response?.data?.message || 'Failed to load subcategory details');
            navigate('/manage-subcategory');
        } finally {
            setFetching(false);
        }
    };

    const validateStep = () => {
        if (step === 1) return true;
        if (step === 2) {
            return subCategoryName.trim() !== "" && categoryId !== null && status !== null;
        }
        return true;
    };

    const validateBasicDetails = () => {
        if (!subCategoryName.trim()) {
            alert("Subcategory name is required");
            return false;
        }
        if (!categoryId) {
            alert("Category is required");
            return false;
        }
        if (!status) {
            alert("Status is required");
            return false;
        }
        return true;
    };

    const nextStep = () => {
        if (step === 2) {
            const isValid = validateBasicDetails();
            if (!isValid) return;
        }
        if (validateStep()) setStep((prev) => Math.min(prev + 1, 2));
        else alert("Please fill all required fields before proceeding.");
    };
    
    const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

    const steps = [
        { label: "Subcategory image", sub: "Enter subcategory image details" },
        { label: "Basic details", sub: "Enter subcategory basic details" },
    ];

    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
            isExisting: false,
            fileName: file.name
        }));
        setImages(newImages);
    };

    // Remove image
    const removeImage = () => {
        setImages([]);
    };

    // Check if data has changed from original
    const hasDataChanged = () => {
        if (!originalData) return false;
        
        // Check for image changes
        const hasImageChanged = images.length === 0 && originalData.image ? true : 
            images.length > 0 && images[0].isExisting === false;
        
        return (
            subCategoryName !== originalData.name ||
            slug !== originalData.slug ||
            description !== originalData.description ||
            status.value !== originalData.status ||
            categoryId !== originalData.category_id ||
            categoryMasterId !== originalData.category_master_id ||
            hasImageChanged
        );
    };

    const handleUpdateSubcategory = async () => {
        if (!validateStep()) {
            alert("Please fill all required fields before updating.");
            return;
        }

        if (!hasDataChanged()) {
            alert("No changes detected.");
            return;
        }

        setLoading(true);
        try {
            // Create FormData
            const formData = new FormData();
            
            // Always append required fields
            formData.append('name', subCategoryName);
            formData.append('category_id', categoryId);
            formData.append('status', status.value);
            
            // Append optional fields (even if they haven't changed)
            // The backend will handle if they need to be updated
            formData.append('slug', (slug || subCategoryName).toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
            formData.append('description', description || '');
            
            // Add category master if exists
            if (categoryMasterId) {
                formData.append('category_master_id', categoryMasterId);
            } else {
                // If you want to clear it when empty, uncomment the next line
                // formData.append('category_master_id', '');
            }

            // Handle image
            if (images.length > 0) {
                if (images[0].file) {
                    // New image uploaded
                    formData.append('image', images[0].file);
                }
                // If it's existing image (isExisting: true), don't append anything
                // The backend will keep the existing image
            } else if (images.length === 0 && originalData.image) {
                // Image was removed - send empty string to indicate removal
                formData.append('image', '');
            }

            console.log('FormData contents:');
            for (let [key, value] of formData.entries()) {
                console.log(`${key}:`, key === 'image' ? `${value.name || 'file'} (${value.type || 'no type'})` : value);
            }

            // For Laravel, we need to use POST with _method=PUT for FormData
            formData.append('_method', 'POST');

            const response = await categoryService.updateSubcategory(id, formData);
            
            console.log('Update subcategory response:', response);
            
            setOpenSnackbar(true);
            setTimeout(() => {
                navigate(`/subcategory-details/${id}`);
            }, 2000);
            
        } catch (error) {
            console.error('Update subcategory error:', error);
            console.error('Error response:', error.response);
            
            // Better error handling
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.values(errors).flat().join('\n');
                alert(`Validation errors:\n${errorMessages}`);
            } else {
                alert(error.response?.data?.message || 'Failed to update subcategory');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    if (fetching) {
        return (
            <div className="manage-member-layout">
                <SideBar />
                <div className="product-details-main">
                    <DetailsHeaderBar
                        title="Edit Subcategory"
                        subtitle="You can edit the subcategory details"
                        breadcrumbs={[
                            {label: "Home", path: "/manage-member"},
                            {label: "Manage Subcategory", path: "/manage-subcategory"},
                            {label: "Edit Subcategory"}
                        ]}
                    />
                    <div style={{textAlign: 'center', padding: '50px'}}>Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-member-layout">
            <SideBar />
            <div className="product-details-main">
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MuiAlert
                        severity="success"
                        icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
                        sx={{
                            backgroundColor: "transparent",
                            backdropFilter: "blur(8px)",
                            color: "#000",
                            border: "2px solid rgba(51, 178, 51, 1)",
                            borderRadius: "10px",
                            boxShadow: "none",
                            fontSize: "15px",
                            padding: "8px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            height: '25px',
                            width: '100%',
                            textAlign: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        Subcategory updated successfully!
                    </MuiAlert>
                </Snackbar>

                <DetailsHeaderBar
                    title="Edit Subcategory"
                    subtitle="You can edit the subcategory details"
                    breadcrumbs={[
                        {label: "Home", path: "/manage-member"},
                        {label: "Manage Subcategory", path: "/manage-subcategory"},
                        {label: "Edit Subcategory"}
                    ]}
                />

                <div className="multi-form-container">
                    <div className="progress-bar">
                        {steps.map((item, i) => {
                            const stepNum = i + 1;
                            const isActive = step === stepNum;
                            const isCompleted = step > stepNum;
                            return (
                                <div key={i} className="progress-step">
                                    <div
                                        className={`circle ${isCompleted ? "completed" : isActive ? "active" : ""}`}
                                    >
                                        {isCompleted ? "✓" : stepNum}
                                    </div>
                                    <div className="step-text">
                                        <p className={`step-label ${isActive ? "active-label" : ""}`}>
                                            {item.label}
                                        </p>
                                        <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                                            {item.sub}
                                        </p>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div
                                            className={`line ${isCompleted ? "line-filled" : "line-empty"}`}
                                        ></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="step-content">
                        {step === 1 && (
                            <div>
                                <h3>Subcategory Image Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Subcategory Image</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImagesChange}
                                            style={{maxWidth:"100%"}}
                                            disabled={loading}
                                        />
                                        <small>Upload a new image to replace the existing one (optional)</small>

                                        <div style={{ display: "flex", gap: 10, marginTop: 10}}>
                                            {images.map((img, index) => (
                                                <div 
                                                    key={index}
                                                    style={{
                                                        position: "relative",
                                                        height: 120,
                                                    }}
                                                >
                                                    <img 
                                                        src={img.preview} 
                                                        alt='subcategory' 
                                                        onClick={() => setPreviewImage(img.preview)}
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            objectFit: "cover",
                                                            borderRadius: 6,
                                                            display: "block",
                                                            marginBottom: 6,
                                                            cursor:"pointer",
                                                        }} 
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={removeImage}
                                                        style={{
                                                            position: "absolute",
                                                            top: 4,
                                                            right: 4,
                                                            background: "#e53935",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: "50%",
                                                            width: 24,
                                                            height: 24,
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: "bold",
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        x
                                                    </button>
                                                </div>
                                            ))}
                                            {images.length === 0 && originalData?.image && (
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <span style={{ color: '#666' }}>
                                                        Current image will be kept
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>       
                        )}
                        {step === 2 && (
                            <div>
                                <h3>Basic Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Subcategory name <span className="required">*</span></label>
                                        <input 
                                            type="text" 
                                            value={subCategoryName}
                                            onChange={(e) => setSubCategoryName(e.target.value)}
                                            placeholder="Enter subcategory name" 
                                            disabled={loading}
                                            required
                                         maxLength={255} />
                                    </div>

                                    <div className="form-group">
                                        <label>Slug</label>
                                        <input 
                                            type="text" 
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            placeholder="Enter slug" 
                                            disabled={loading}
                                         maxLength={255} />
                                    </div>

                                    <div className="form-group">
                                        <label>Category <span className="required">*</span></label>
                                        <Select
                                            options={categories}
                                            value={categories.find(cat => cat.value === categoryId)}
                                            onChange={(selected) => setCategoryId(selected ? selected.value : null)}
                                            placeholder="Select category"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            required
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Category Master</label>
                                        <Select
                                            options={categoryMasterOptions}
                                            value={categoryMasterOptions.find(cat => cat.value === categoryMasterId)}
                                            onChange={(selected) => setCategoryMasterId(selected ? selected.value : null)}
                                            placeholder="Select category master"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            isClearable={true}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Status <span className="required">*</span></label>
                                        <Select
                                            options={statusOptions}
                                            value={status}
                                            onChange={setStatus}
                                            placeholder="Select status"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            required
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>

                                    <div className="form-group" style={{gridColumn: 'span 2'}}>
                                        <label>Description</label>
                                        <textarea
                                            className="note-textarea"
                                            rows="4"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Enter subcategory description"
                                            style={{padding:"10px"}}
                                            disabled={loading}
                                         maxLength={65535} />  
                                    </div>
                                </div>            
                            </div>        
                        )}
                    </div>

                    <div className="btn-row">
                        {step > 1 && (
                            <button className="btn prev" onClick={prevStep} disabled={loading}>
                                Previous
                            </button>
                        )}
                        {step < 2 && (
                            <button 
                                className="btn next" 
                                onClick={nextStep}
                                disabled={!validateStep() || loading}
                                style={{
                                    opacity: validateStep() ? 1 : 0.6,
                                    cursor: validateStep() ? 'pointer' : 'not-allowed'
                                }}
                            >
                                Next
                            </button>
                        )}
                        {step === 2 && (
                            <button 
                                className="btn next" 
                                onClick={handleUpdateSubcategory}
                                disabled={!validateStep() || loading || !hasDataChanged()}
                                style={{
                                    opacity: (validateStep() && hasDataChanged()) ? 1 : 0.6,
                                    cursor: (validateStep() && hasDataChanged()) ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {loading ? 'Updating...' : 'Update Subcategory'}
                            </button>
                        )}
                    </div>
                
                    {previewImage && (
                        <div
                            onClick={() => setPreviewImage(null)}
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                background: "rgba(0,0,0,0.8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div style={{ position: "relative" }}>
                                <img
                                    src={previewImage}
                                    alt="Preview"
                                    style={{
                                        maxWidth: "90vw",
                                        maxHeight: "90vh",
                                        borderRadius: 10,
                                    }}
                                />
                                <button
                                    onClick={() => setPreviewImage(null)}
                                    style={{
                                        position: "absolute",
                                        top: -10,
                                        right: -10,
                                        background: "#e53935",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: 30,
                                        height: 30,
                                        cursor: "pointer",
                                        fontSize: 16,
                                        fontWeight: "bold",
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}
                </div>  
            </div>
        </div>
    )
}

export default EditSubCategory;
